import Card from 'components/card';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteProduction, getProductions } from 'interfaces/productions';
import PlannedProductionItem from './components/PlannedProductionItem';
import PlannedProductionPreviewModal from './components/productionPreview';
import DynamicProductionForm from 'components/production/DynamicProductionForm';
import { useDisclosure } from '@chakra-ui/hooks';
import { Modal, ModalBody, ModalContent, ModalOverlay, ModalHeader } from '@chakra-ui/modal';
import DeleteModal from 'components/actions/Delete';
import { MdFactory } from 'react-icons/md';
import fetchClient from 'interfaces/fetchClient';


const PlannedProductions = () => {
    const [productions, setProductions] = useState<any>([]);
    const [production, setProduction] = useState<any>(null);
    const [selectedProductionId, setSelectedProductionId] = useState<string | null>(null);
    const [toDelete, setToDelete] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [config, setConfig] = useState<any>(null);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isOpenAdvance, onOpen: onOpenAdvance, onClose: onCloseAdvance } = useDisclosure();
    const nav = useNavigate();
    const {
        isOpen: isOpenDelete,
        onOpen: onOpenDelete,
        onClose: onCloseDelete,
    } = useDisclosure();

    const initProductions = async () => {
        setIsLoading(true);
        const prods = await getProductions();
        if (!prods) {
            nav('/auth/login');
            return;
        }
        setProductions(prods.data);
        
        // Retrieve config for custom field names
        try {
            const response = await fetchClient.get('/productions/config/workflow', {
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem('token'),
                },
            });
            setConfig(response.data);
        } catch (error) {
            console.error('Error fetching config:', error);
        }
        
        setIsLoading(false);
    };

    const delProduction = async (id: string) => {
        const rest = await deleteProduction(id);
        if (!rest) return;
        if (rest.error) {
            console.log(rest.error);
            return;
        }
        onCloseDelete();
        const remaining = productions.filter(
            (p: any) => p._id !== id && p.status === 'scheduled',
        );
        if (remaining.length <= 0) {
            nav('/stock/production');
            return;
        } else setProductions(remaining);
    };

    useEffect(() => {
        initProductions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [processingProductions, setProcessingProductions] = useState<Set<string>>(new Set());

    const handleView = async (prod: any) => {
        // Prevent double-click race condition
        if (processingProductions.has(prod._id)) {
            return;
        }
        setProcessingProductions(prev => new Set(prev).add(prod._id));

        // Verify configuration to know if we should display the modal
        try {
            const response = await fetchClient.get('/productions/config/workflow', {
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem('token'),
                },
            });
            const config = response.data;
            
            // If nbSteps = 1, advance directly without modal
            if (config.nbSteps === 1) {
                setIsLoading(true);
                await fetchClient.post(
                    `/productions/advance/${prod._id}`,
                    { customData: {} },
                    {
                        headers: {
                            'Authorization': 'Bearer ' + localStorage.getItem('token'),
                        },
                    }
                );
                // Refresh the list
                await initProductions();
                return;
            } 
            // If the production is "scheduled" and nbSteps > 1, advance without form (move to custom_step)
            else if (prod.status === 'scheduled') {
                setIsLoading(true);
                await fetchClient.post(
                    `/productions/advance/${prod._id}`,
                    { customData: {} },
                    {
                        headers: {
                            'Authorization': 'Bearer ' + localStorage.getItem('token'),
                        },
                    }
                );
                // Refresh the list to show the production in "custom_step" state
                await initProductions();
                return;
            }
            // If the production is waiting for a step (custom_step or pending_validation), open the form
            else {
                setSelectedProductionId(prod._id);
                onOpenAdvance();
            }
        } catch (error) {
            console.error('Error checking config or advancing production:', error);
            // Show error message instead of opening modal
            alert('Erreur lors du lancement de la production. Veuillez réessayer.');
            setIsLoading(false);
        } finally {
            // Remove from processing set
            setProcessingProductions(prev => {
                const newSet = new Set(prev);
                newSet.delete(prod._id);
                return newSet;
            });
        }
    };

    const handleDeleteClick = (prod: any) => {
        setToDelete(prod);
        onOpenDelete();
    };

    const scheduledProductions = productions.filter((p: any) => p.status === 'scheduled');
    const inProgressProductions = productions.filter((p: any) => 
        p.status === 'custom_step' || p.status === 'pending_validation'
    );
    const allActiveProductions = [...scheduledProductions, ...inProgressProductions];
    const totalQuantity = allActiveProductions.reduce((acc: number, p: any) => acc + (p.quantity || 0), 0);

    return (
        <div className="w-full h-full min-h-[85vh]">
            {/* Header Section */}
            <Card extra="w-full p-5 mb-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-bold text-navy-700 dark:text-white">
                            Vos productions planifiées
                        </h2>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                            Gérez et lancez vos productions prêtes à être réalisées
                        </p>
                    </div>
                    {scheduledProductions.length > 0 && (
                        <div className="hidden md:flex items-center gap-4">
                            <div className="text-center">
                                <p className="text-xs text-gray-500 dark:text-gray-400">Productions</p>
                                <p className="text-2xl font-bold text-toola-500">
                                    {allActiveProductions.length}
                                </p>
                            </div>
                            {inProgressProductions.length > 0 && (
                                <div className="text-center">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">En attente</p>
                                    <p className="text-2xl font-bold text-blue-500">
                                        {inProgressProductions.length}
                                    </p>
                                </div>
                            )}
                            {totalQuantity > 0 && (
                                <div className="text-center">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Unités</p>
                                    <p className="text-2xl font-bold text-toola-500">
                                        {totalQuantity}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Card>

            <PlannedProductionPreviewModal
                isOpen={isOpen}
                onClose={onClose}
                production={production}
            />

            {/* Advance Production Modal */}
            <Modal 
                isOpen={isOpenAdvance} 
                onClose={() => {
                    setSelectedProductionId(null);
                    onCloseAdvance();
                }}
            >
                <ModalOverlay className="bg-[#5a5c5a]/70" />
                <ModalContent className="!m-auto !w-max min-w-[400px] !max-w-[800px]">
                    <ModalHeader className="!p-5 !pb-3">
                        <h4 className="text-xl font-bold text-navy-700 dark:text-white">
                            Avancer la production
                        </h4>
                    </ModalHeader>
                    <ModalBody className="!p-5 !pt-0">
                        {selectedProductionId && (
                            <DynamicProductionForm
                                productionId={selectedProductionId}
                                onSuccess={() => {
                                    setSelectedProductionId(null);
                                    onCloseAdvance();
                                    initProductions(); // Refresh the list
                                }}
                                onCancel={() => {
                                    setSelectedProductionId(null);
                                    onCloseAdvance();
                                }}
                            />
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>

            <DeleteModal
                isOpen={isOpenDelete}
                onClose={onCloseDelete}
                description="Êtes-vous sûr de vouloir supprimer cette production ?"
                context="de production"
                deleteFunction={() => {
                    delProduction(toDelete._id);
                }}
            />

            {/* List of productions */}
            {isLoading ? (
                <Card extra="w-full p-5 border border-gray-200 dark:border-navy-700">
                    <div className="flex flex-col items-center justify-center py-20">
                        <div className="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-toola-500"></div>
                        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Chargement des productions...</p>
                    </div>
                </Card>
            ) : allActiveProductions.length === 0 ? (
                <Card extra="w-full p-5 border border-gray-200 dark:border-navy-700">
                    <div className="flex w-full flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50/50 py-20 dark:border-navy-600 dark:bg-navy-800/50">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-navy-700">
                            <MdFactory className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                        </div>
                        <p className="mt-4 text-base font-bold text-gray-700 dark:text-gray-300">
                            Aucune production planifiée
                        </p>
                        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                            Planifiez une nouvelle production pour commencer
                        </p>
                    </div>
                </Card>
            ) : (
                <div className="space-y-6">
                    {/* Section: Productions step in progress */}
                    {inProgressProductions.length > 0 && (
                        <div>
                            <div className="mb-3 flex items-center gap-2">
                                <div className="h-1 w-1 rounded-full bg-blue-500"></div>
                                <h3 className="text-lg font-bold text-navy-700 dark:text-white">
                                    Productions en attente d'étapes
                                </h3>
                                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                                    {inProgressProductions.length}
                                </span>
                            </div>
                            <Card extra="w-full p-5 border-2 border-blue-200 dark:border-blue-800">
                                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                                    {inProgressProductions.map((production: any) => (
                                        <PlannedProductionItem
                                            key={production._id}
                                            production={production}
                                            config={config}
                                            onView={() => handleView(production)}
                                            onDelete={() => handleDeleteClick(production)}
                                        />
                                    ))}
                                </div>
                            </Card>
                        </div>
                    )}

                    {/* Section: Scheduled productions */}
                    {scheduledProductions.length > 0 && (
                        <div>
                            <div className="mb-3 flex items-center gap-2">
                                <div className="h-1 w-1 rounded-full bg-green-500"></div>
                                <h3 className="text-lg font-bold text-navy-700 dark:text-white">
                                    Productions planifiées
                                </h3>
                                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-100 text-xs font-bold text-green-600 dark:bg-green-900/30 dark:text-green-400">
                                    {scheduledProductions.length}
                                </span>
                            </div>
                            <Card extra="w-full p-5 border border-gray-200 dark:border-navy-700">
                                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                                    {scheduledProductions.map((production: any) => (
                                        <PlannedProductionItem
                                            key={production._id}
                                            production={production}
                                            config={config}
                                            onView={() => handleView(production)}
                                            onDelete={() => handleDeleteClick(production)}
                                        />
                                    ))}
                                </div>
                            </Card>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default PlannedProductions;
